import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Crown } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

interface LimitReachedDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  resourceLabel?: string
  message?: string
}

export function LimitReachedDialog({
  open,
  onOpenChange,
  resourceLabel,
  message,
}: LimitReachedDialogProps) {
  const navigate = useNavigate()

  const defaultMessage = resourceLabel
    ? `Você atingiu o limite de ${resourceLabel} do plano Free. Faça upgrade para o plano Pro e tenha acesso ilimitado.`
    : 'Você atingiu o limite do seu plano Free. Faça upgrade para o plano Pro e tenha acesso ilimitado.'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} data-testid='limit-reached-dialog'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Crown className='h-5 w-5 text-amber-500' />
            Limite do plano atingido
          </DialogTitle>
          <DialogDescription>{message || defaultMessage}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          <Button
            onClick={() => {
              onOpenChange(false)
              navigate('/assinatura')
            }}
            data-testid='limit-reached-upgrade-button'
          >
            <Crown className='mr-2 h-4 w-4' />
            Fazer upgrade para Pro
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Singleton state para controlar o dialog globalmente
let showLimitDialogFn: ((message?: string, resourceLabel?: string) => void) | null = null

// eslint-disable-next-line react-refresh/only-export-components
export function setLimitDialogHandler(
  handler: ((message?: string, resourceLabel?: string) => void) | null,
) {
  showLimitDialogFn = handler
}

// eslint-disable-next-line react-refresh/only-export-components
export function showLimitReachedDialog(message?: string, resourceLabel?: string) {
  showLimitDialogFn?.(message, resourceLabel)
}

export function LimitReachedDialogProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  const [dialogMessage, setDialogMessage] = useState<string | undefined>()
  const [dialogResourceLabel, setDialogResourceLabel] = useState<string | undefined>()

  useEffect(() => {
    setLimitDialogHandler((message, resourceLabel) => {
      setDialogMessage(message)
      setDialogResourceLabel(resourceLabel)
      setOpen(true)
    })

    return () => setLimitDialogHandler(null)
  }, [])

  return (
    <>
      {children}
      <LimitReachedDialog
        open={open}
        onOpenChange={setOpen}
        message={dialogMessage}
        resourceLabel={dialogResourceLabel}
      />
    </>
  )
}
